import { Expose, Exclude, Transform } from 'class-transformer'
import { DocumentSnapshot } from 'firebase/firestore'
import { _FirebaseDocument, anyToDate } from './Models'
import { parseRevisionFacts, toReportEntryIdentification, type ReportEntryIdentification, type ReportEntryRevisionFacts } from './ReportEntryV2'
import { parseLocalizedText, toConfidenceTier, toDatePrecision, type ConfidenceTier, type DatePrecision, type LocalizedText } from './registry'
import { parseCarriedPosition, type LinePositionV3 } from './ReportPositionV3'

/**
 * v3 report card (MYAC-1496 lot 1, contract of 01/10/2026): ONE card per text
 * and followed edition, with the watched lines on it. Written by the
 * regulatory-monitoring v3 engine at
 * `projects/{pid}/regulation_reports/{rid}/cards/{card_id}`; the report view
 * reads it when the report's `schema_version === 'v3'`. Every closed
 * vocabulary degrades to `'unknown'`: a front deployed before a backend that
 * emits a newer value must never crash on it.
 */

export const REPORT_GROUPS_V3 = [
  'deadline_passed',
  'text_updated',
  'for_information',
  'no_change',
  'identification',
] as const
export type ReportGroupV3 = (typeof REPORT_GROUPS_V3)[number] | 'unknown'

export function toReportGroupV3(v: unknown): ReportGroupV3 {
  return (REPORT_GROUPS_V3 as readonly string[]).includes(v as string) ? (v as ReportGroupV3) : 'unknown'
}

const EVENT_TYPES = [
  'new_edition',
  'amendment',
  'replacement',
  'withdrawal',
  'en_adoption',
  'harmonised_cited',
  'harmonised_removed',
  'edition_in_preparation',
] as const
export type ReportEventTypeV3 = (typeof EVENT_TYPES)[number] | 'unknown'

const EDITION_SITUATIONS = ['not_specified', 'edition_not_found', 'other_form', 'being_added'] as const
export type EditionSituationV3 = (typeof EDITION_SITUATIONS)[number] | 'unknown'

export interface ReportEventTargetV3 {
  family_id: string
  revision_key: string | null
  label: string
}

export interface ReportEventV3 {
  type: ReportEventTypeV3
  date: Date | null
  /** How much of `date` the registry read (D75): `month` = stored YYYY-MM-01,
   *  `year` = YYYY-01-01, their day never shown. Optional in fixtures; the
   *  parser always sets it (`day` when the doc has none). Read it through
   *  `reportDatePrecision`. */
  date_precision?: DatePrecision
  new_in_period: boolean
  target: ReportEventTargetV3 | null
}

/** What a card's deadline is (MYAC-1496, 06/10): the badge names it. */
export const DEADLINE_KINDS = ['transition_end', 'presumption_end'] as const
export type DeadlineKindV3 = (typeof DEADLINE_KINDS)[number] | 'unknown'

export interface ReportDeadlineV3 {
  kind: DeadlineKindV3
  date: Date
  /** As on `ReportEventV3` (D75). */
  date_precision?: DatePrecision
  /** The edition the date belongs to. */
  edition: ReportEventTargetV3 | null
}

export interface ReportCardLineV3 {
  row_id: string
  designation: string
  typed_edition: string | null
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function parseTarget(raw: unknown): ReportEventTargetV3 | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (!str(r.family_id)) return null
  return { family_id: str(r.family_id), revision_key: str(r.revision_key) || null, label: str(r.label) }
}

/** `date_precision` of an event or a deadline (D75, CMS `ReportEvent` /
 *  `ReportDeadline`): absent = `day`, the meaning of every card written before
 *  it; a value this build does not know = `unknown`, shown like `day`. */
function parseDatePrecision(raw: unknown): DatePrecision {
  return raw === undefined || raw === null ? 'day' : toDatePrecision(raw)
}

/** The precision of an event's or a deadline's date; `day` when unset. */
export function reportDatePrecision(x: { date_precision?: DatePrecision } | null | undefined): DatePrecision {
  return x?.date_precision ?? 'day'
}

function parseEvent(raw: unknown): ReportEventV3 | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const type = (EVENT_TYPES as readonly string[]).includes(r.type as string) ? (r.type as ReportEventTypeV3) : 'unknown'
  return {
    type,
    date: anyToDate({ obj: r, key: 'date' }) ?? null,
    date_precision: parseDatePrecision(r.date_precision),
    new_in_period: r.new_in_period === true,
    target: parseTarget(r.target),
  }
}

function parseDeadline(raw: unknown): ReportDeadlineV3 | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const date = anyToDate({ obj: r, key: 'date' })
  if (!date) return null
  const kind = (DEADLINE_KINDS as readonly string[]).includes(r.kind as string) ? (r.kind as DeadlineKindV3) : 'unknown'
  return { kind, date, date_precision: parseDatePrecision(r.date_precision), edition: parseTarget(r.edition) }
}

function parseLine(raw: unknown): ReportCardLineV3 | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (!str(r.row_id)) return null
  return { row_id: str(r.row_id), designation: str(r.designation), typed_edition: str(r.typed_edition) || null }
}

function list<T>(x: { obj: Record<string, unknown>; key: string }, parse: (raw: unknown) => T | null): T[] {
  const raw = x.obj[x.key]
  return Array.isArray(raw) ? raw.map(parse).filter((v): v is T => v !== null) : []
}

@Exclude()
export class ReportCardV3 extends _FirebaseDocument {
  @Expose()
  @Transform(({ obj, key }) => str(obj[key]) || null)
  public family_id: string | null = null

  @Expose()
  @Transform(({ obj, key }) => str(obj[key]) || null)
  public revision_key: string | null = null

  @Expose()
  public designation: string = ''

  @Expose()
  @Transform(({ obj, key }) => (obj[key] ? parseLocalizedText(obj[key]) : null))
  public title: LocalizedText | null = null

  @Expose()
  @Transform((x) => list(x, parseLine))
  public lines: ReportCardLineV3[] = []

  @Expose()
  @Transform(({ obj, key }) => toReportGroupV3(obj[key]))
  public group: ReportGroupV3 = 'unknown'

  @Expose()
  @Transform((x) => list(x, parseEvent))
  public events: ReportEventV3[] = []

  @Expose()
  @Transform(parseRevisionFacts)
  public revision: ReportEntryRevisionFacts | null = null

  @Expose()
  @Transform(anyToDate)
  public deadline: Date | null = null

  /** Every dated deadline behind `deadline`, earliest first, with its kind
   *  and edition; empty on a card written before the kinds were kept. */
  @Expose()
  @Transform((x) => list(x, parseDeadline))
  public deadlines: ReportDeadlineV3[] = []

  @Expose()
  @Transform(({ obj, key }) => (obj[key] ? toReportEntryIdentification(obj[key]) : null))
  public identification: ReportEntryIdentification | null = null

  @Expose()
  @Transform(({ obj, key }) =>
    obj[key] ? ((EDITION_SITUATIONS as readonly string[]).includes(obj[key]) ? obj[key] : 'unknown') : null,
  )
  public edition_situation: EditionSituationV3 | null = null

  @Expose()
  @Transform(({ obj, key }) => str(obj[key]) || null)
  public typed_edition: string | null = null

  @Expose()
  @Transform(({ obj, key }) => parseTarget(obj[key]))
  public proposed: ReportEventTargetV3 | null = null

  @Expose()
  @Transform(({ obj, key }) => toConfidenceTier(obj[key]))
  public tier: ConfidenceTier = 'unknown'

  @Expose()
  @Transform(anyToDate)
  public last_verified: Date | null = null

  @Expose()
  @Transform(({ obj, key }) => obj[key] === true)
  public stale: boolean = false

  /** Positions carried from the last finalised report while the card's
   *  fingerprint held (step 7, D2 A). */
  @Expose()
  @Transform((x) => list(x, parseCarriedPosition))
  public carried_positions: LinePositionV3[] = []

  collection(): string {
    return 'cards'
  }

  static fromFirebase(o: DocumentSnapshot): ReportCardV3 | null {
    return _FirebaseDocument._fromFirebase(ReportCardV3, o)
  }
}
